import { forwardRef, useState } from "react";
import { CODE_LENGTH, cleanCode } from "utils/trackBooking";

/**
 * The six-digit code field, shared by Track my booking (1NOW-11157) and the checkout's email code (1NOW-10770).
 * The classes are Track my booking's: the tenant's tokens only.
 */
export const labelCls = "block text-sm font-semibold text-text-color mb-1.5";
export const buttonCls =
  "inline-flex min-w-[180px] items-center justify-center rounded-full bg-button-color [.design-a_&]:bg-primary-6000 [.design-a_&]:text-on-primary px-7 py-3 text-sm font-extrabold text-white shadow-sm transition-all duration-200 hover:bg-primary-brand hover:text-on-primary disabled:opacity-70";
export const linkButtonCls = "text-sm font-semibold text-text-color underline disabled:no-underline disabled:opacity-60";
/** 125 → "2:05": the wait between codes runs to minutes. */
export const clock = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/**
 * 1NOW-11211: six boxes, OTP style. One real input lies over them (invisible), so typing, Backspace, paste and
 * the phone's one-time-code autofill all work as on any field; the boxes only draw it, the next one highlighted.
 * `invalid` outlines the boxes in red (the code was refused).
 */
const EmailCodeInput = forwardRef<
  HTMLInputElement,
  { id?: string; value: string; onChange: (next: string) => void; invalid?: boolean }
>(({ id = "track-code", value, onChange, invalid }, ref) => {
  const [focused, setFocused] = useState(false);
  return (
    <>
      <label className={labelCls} htmlFor={id}>
        6-digit code
      </label>
      <div className="relative w-full max-w-[22rem]">
        <div className="grid grid-cols-6 gap-1.5 sm:gap-2" aria-hidden="true" data-testid="code-boxes">
          {Array.from({ length: CODE_LENGTH }, (_, i) => {
            const active = focused && i === Math.min(value.length, CODE_LENGTH - 1);
            return (
              <span
                key={i}
                data-active={active || undefined}
                className={`flex h-12 items-center justify-center rounded-xl border bg-bg-color text-xl font-bold text-text-color sm:h-14 sm:text-2xl ${
                  invalid ? "border-red-500" : active ? "border-primary-600 ring-2 ring-primary-600/30" : "border-text-color/50"
                }`}
              >
                {value[i] ?? ""}
              </span>
            );
          })}
        </div>
        <input
          id={id}
          name="code"
          ref={ref}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LENGTH}
          value={value}
          onChange={(e) => onChange(cleanCode(e.target.value))}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          aria-invalid={!!invalid}
          className="absolute inset-0 h-full w-full cursor-text border-0 bg-transparent text-transparent caret-transparent opacity-0 focus:ring-0"
        />
      </div>
    </>
  );
});
EmailCodeInput.displayName = "EmailCodeInput";

export default EmailCodeInput;
